import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles, Typography } from '@material-ui/core';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import ConditionalTooltip from '../conditionalTooltip';
import Tooltip from '../tooltip';
import CustomButton from '../customButton';
import RichTextPreview from '../richTextPreview';
import { ReactComponent as WarningIcon } from '../../../assets/icons/warning-triangle-rounded.svg';
import { ellipsis } from '../../../constants/helperCssRules';
import { onScrollIntoView } from '../../../utility/uiUtils';
import {
  formatDate,
  isDateInPast,
  isDateSoonToExpire,
} from '../../../utility/dateUtils';

const FULL_DATE_FORMAT = 'MMM dd, yyyy';

const getFileNameFromPath = filePath => {
  if (!filePath || typeof filePath !== 'string') {
    return '';
  }

  const sanitizedFilePath = filePath.split('?')[0];
  const fileName = sanitizedFilePath.split('/').pop();

  if (!fileName) {
    return '';
  }

  const lastDotIndex = fileName.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0;
  const baseName = hasExtension ? fileName.slice(0, lastDotIndex) : fileName;
  const extension = hasExtension ? fileName.slice(lastDotIndex) : '';
  const baseWithoutLeadingDigits = baseName.replace(/^\d+/, '');

  if (!baseWithoutLeadingDigits) {
    return fileName;
  }

  return baseWithoutLeadingDigits + extension;
};

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    backgroundColor: primary.bluish8,
    border: `1px solid ${primary.bluish8}`,
    borderRadius: 4,
    height: 66,
    padding: spacing(3, 6),
    display: 'grid',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridTemplateColumns: '186px 170px 135px 135px 64px',
    gap: 40,
  },
  expired: {
    border: `1px solid ${primary.red1}`,
  },
  soonToExpire: {
    border: `1px solid ${primary.orange}`,
  },
  content: {
    display: 'grid',
    gap: spacing(0.5),
  },
  title: {
    color: primary.bluish3,
    maxWidth: 'fit-content',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 187,
  },
  documentLabel: {
    color: primary.main,
    cursor: 'pointer',
    maxWidth: 'fit-content',
  },
  value: {
    color: primary.bluish1,
  },
  expiryDate: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  expiredIcon: {
    display: 'block',
    width: 16,
    height: 16,
    flexShrink: 0,
    marginTop: spacing(-0.5),
    '&&': {
      cursor: 'default',
    },
    '& path': {
      fill: primary.red1,
    },
  },
  soonToExpireIcon: {
    display: 'block',
    width: 16,
    height: 16,
    flexShrink: 0,
    marginTop: spacing(-0.5),
    '&&': {
      cursor: 'default',
    },
    '& path': {
      fill: primary.orange,
    },
  },
  actionButton: {
    maxHeight: 22,
    padding: spacing(1, 3),
    fontSize: 12,
    lineHeight: '14px',
    fontFamily: 'ProximaNova-Bold',
  },
  descriptionPreview: {
    ...ellipsis(),
    display: '-webkit-box',
    '-webkit-line-clamp': 1,
    '-webkit-box-orient': 'vertical',
    '& > *': {
      margin: 0,
    },
    whiteSpace: 'normal',
  },
  tooltip: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'visible',
  },
  tooltipLabel: {
    overflow: 'visible',
    whiteSpace: 'normal',
  },
}));

const DocumentTypeCard = ({
  translations,
  documentType,
  expireWarningDays = undefined,
  canManage,
  isCurrentUserProfile,
  shouldScrollIntoView = false,
  getFileUrl,
  onOpenDocumentDrawer,
}) => {
  const classes = useStyles();
  const cardRef = useRef(null);
  const buttonLabel = documentType.document
    ? translations.edit
    : translations.add;
  const fileUrl = documentType.document?.file;
  const fileName = getFileNameFromPath(fileUrl) || '-';
  const canManageDocument =
    canManage || (documentType.self_upload && isCurrentUserProfile);
  const expireDate = documentType.document?.expire_date;
  const isExpired = expireDate ? isDateInPast(expireDate) : false;
  const isSoonToExpire = isDateSoonToExpire(expireDate, expireWarningDays);
  const signingDate = documentType.document?.signing_date
    ? formatDate(documentType.document?.signing_date, FULL_DATE_FORMAT)
    : '-';
  const expiryDate = expireDate
    ? formatDate(expireDate, FULL_DATE_FORMAT)
    : '-';

  const renderDescription = (description, { forTooltip = false } = {}) => (
    <RichTextPreview
      variant="body1"
      className={classNames(classes.value, {
        [classes.descriptionPreview]: !forTooltip,
      })}
      customLabelClass={classNames({ [classes.tooltipLabel]: forTooltip })}
      isEllipsis={!forTooltip}
      text={description || '-'}
    />
  );

  useEffect(() => {
    if (cardRef && shouldScrollIntoView) {
      onScrollIntoView(cardRef, { block: 'center' });
    }
  }, [shouldScrollIntoView]);

  return (
    <div
      ref={cardRef}
      className={classNames(classes.root, {
        [classes.expired]: isExpired,
        [classes.soonToExpire]: isSoonToExpire,
      })}
    >
      <div className={classes.content}>
        <div className={classes.titleWrapper}>
          <TextBoxWithTooltip
            variant="subtitle2"
            className={classes.title}
            text={`${documentType.name}`}
          />
          <Typography variant="subtitle2" className={classes.title}>
            {documentType.is_mandatory ? ' *' : ''}
          </Typography>
        </div>
        <TextBoxWithTooltip
          variant="body1"
          className={classes.documentLabel}
          text={fileName}
          isClickable={canManageDocument}
          onClickHandler={() =>
            window.open(getFileUrl(fileUrl), '_blank', 'noopener noreferrer')
          }
        />
      </div>
      <div className={classes.content}>
        <Typography variant="subtitle2" className={classes.title}>
          {translations.description}
        </Typography>
        <ConditionalTooltip
          className={classes.tooltip}
          message={renderDescription(documentType.document?.description, {
            forTooltip: true,
          })}
          addTooltip={Boolean(documentType.document?.description)}
          interactive
        >
          {renderDescription(documentType.document?.description)}
        </ConditionalTooltip>
      </div>
      <div className={classes.content}>
        <Typography variant="subtitle2" className={classes.title}>
          {translations.signingDateLabel}
        </Typography>
        <Typography variant="body1" className={classes.value}>
          {signingDate}
        </Typography>
      </div>
      <div className={classes.content}>
        <Typography variant="subtitle2" className={classes.title}>
          {translations.expiryDateLabel}
        </Typography>
        <div className={classes.expiryDate}>
          <Typography variant="body1" className={classes.value}>
            {expiryDate}
          </Typography>
          {isExpired && (
            <Tooltip
              text={translations.expiredExpiryDateTooltip}
              icon={WarningIcon}
              customIconClass={classes.expiredIcon}
            />
          )}
          {isSoonToExpire && (
            <Tooltip
              text={translations.soonToExpireTooltip}
              icon={WarningIcon}
              customIconClass={classes.soonToExpireIcon}
            />
          )}
        </div>
      </div>
      {canManageDocument && (
        <CustomButton
          className={classes.actionButton}
          type="withTextLightRounded"
          onClick={onOpenDocumentDrawer(documentType)}
        >
          {buttonLabel}
        </CustomButton>
      )}
    </div>
  );
};

DocumentTypeCard.propTypes = {
  translations: PropTypes.object.isRequired,
  documentType: PropTypes.object.isRequired,
  expireWarningDays: PropTypes.number,
  canManage: PropTypes.bool.isRequired,
  isCurrentUserProfile: PropTypes.bool.isRequired,
  shouldScrollIntoView: PropTypes.bool,
  getFileUrl: PropTypes.func.isRequired,
  onOpenDocumentDrawer: PropTypes.func.isRequired,
};

export default DocumentTypeCard;
